import { emitter, EventType } from '@/utils/emitter.utils';
import { round } from 'lodash-es';
import { KonvaTransform } from './konva.transform';
import { KonvaUtils } from './konva.utils';
import { KonvaGuideline } from './konva.guideline';
import KonvaService from '.';
import { Shape, ShapeType } from '../shape';
import { Stage } from 'konva/lib/Stage';
import { Node } from 'konva/lib/Node';
import { Transformer } from 'konva/lib/shapes/Transformer';
import showToast from '@/components/common/toast';
import { lockedIdsInCurShapeTree } from '@/hooks';
interface DragingShape {
  // shape名称
  name: string;
  // 拖拽时shape的起始位置
  x?: number;
  y?: number;
  // children?: DragingShape[];
}

export class KonvaDrag {
  /**
   * 拖拽最小阈值
   *
   * @static
   * @memberof KonvaDrag
   */
  static THRESHOLD = 8;
  /**
   * 是否启用guideline
   */
  guideline = true;

  static DRAGGING = false;

  private konvaService: KonvaService;
  constructor(konvaService: KonvaService) {
    this.konvaService = konvaService;
  }

  /**
   * 当前拖拽的节点名称
   * 当拖拽多个时，防止混乱，仅以一个节点的拖拽数据为准
   */
  private draggingNode:
    | undefined
    | {
        shapeName: string;
        x0: number;
        y0: number;
        absolute: boolean;
        absoluteX0: number;
        absoluteY0: number;
        node: Node;
      };

  /**
   * 记录当前拖拽的shapes的数据
   */
  private draggingShapes: DragingShape[] = [];

  /**
   * 开启拖拽
   * @param node 鼠标拖拽的节点
   * @param moveNodes 需要更新位置的节点
   */
  private setDragStart(node: Node, moveNodes: Node[], tr: Transformer) {
    const shapeName = node.name();
    KonvaDrag.DRAGGING = true;

    // console.log('=== setDragStart DEBUG ===');
    // console.log('node.name():', node.name());
    // console.log('moveNodes:', moveNodes.map(n => n.name()));

    if (!this.draggingNode?.shapeName) {
      // 判断是否使用absolute
      // 拖拽的元素非选中状态
      const absolute = !tr.nodes().includes(node);
      const { x: x0, y: y0 } = !absolute
        ? node.position()
        : node.absolutePosition();

      const absPosi = node.absolutePosition();
      this.draggingNode = {
        shapeName,
        x0,
        y0,
        absolute,
        absoluteX0: absPosi.x,
        absoluteY0: absPosi.y,
        node
      };
      // console.log('draggingNode:', this.draggingNode);
    }

    this.draggingShapes = [];

    const _getDraggingShape = (shape: Shape, node: Node) => {
      // 判断节点是否在 Group 中
      const isInGroup = node.parent && node.parent.getType?.() === 'Group';

      // 对于成组的元素，使用 konva 节点的实际位置（相对于父容器）
      // 对于非成组的元素，使用 shape.attrs 中的位置
      const position = isInGroup
        ? node.position()
        : { x: shape?.attrs.x ?? 0, y: shape?.attrs.y ?? 0 };

      const res: DragingShape = {
        name: shape.id,
        x: position.x,
        y: position.y
      };
      // console.log(
      //   '  _getDraggingShape - isInGroup:',
      //   isInGroup,
      //   'position:',
      //   position
      // );
      return res;
    };

    // 设置此次拖拽控制的元素
    // 如果拖拽的不在tr中，则单独拖拽此node
    // 如果在的话，则拖拽tr中的数据
    moveNodes.forEach((node) => {
      const name = node.name();
      const shape = Shape.getShape(name);
      // console.log(
      //   'moveNode name:',
      //   name,
      //   'shape:',
      //   shape?.id,
      //   'type:',
      //   shape?.type
      // );
      if (shape) this.draggingShapes.push(_getDraggingShape(shape, node));
    });
    // console.log('draggingShapes:', this.draggingShapes);
    // console.log('=========================');
  }

  private getOffset(node: Node) {
    const shapeName = node.name();
    if (this.draggingNode?.shapeName !== shapeName) {
      return;
    }
    const { x: x1, y: y1 } = !this.draggingNode.absolute
      ? node.position()
      : node.absolutePosition();

    const offsetX = x1 - this.draggingNode.x0;
    const offsetY = y1 - this.draggingNode.y0;

    // 确保偏移量为整数
    return {
      offsetX: round(offsetX, 0),
      offsetY: round(offsetY, 0)
    };
  }

  private setPosiOnDrag(node: Node, stage: Stage) {
    const res = this.getOffset(node);
    if (!res) return;
    const { offsetX = 0, offsetY = 0 } = res;

    // console.log('=== setPosiOnDrag DEBUG ===');
    // console.log('offsetX, offsetY:', offsetX, offsetY);

    // 更新shape上属性
    this.draggingShapes.forEach((item) => {
      const { name, x = 0, y = 0 } = item;
      const shape = Shape.getShape(name);
      const _node = stage.findOne('.' + name);

      // console.log('Updating shape:', name);
      // console.log('  original x, y:', x, y);
      // console.log(
      //   '  _node:',
      //   _node?.name(),
      //   '_node parent:',
      //   _node?.parent?.getType?.()
      // );

      // 判断节点是否在 Group 中
      const isInGroup =
        _node && _node.parent && _node.parent.getType?.() === 'Group';

      const _x = x + offsetX;
      const _y = y + offsetY;

      // console.log('  new konva position (relative to parent):', _x, _y);

      // 对于成组的图片，需要特殊处理
      // konva 节点的位置是相对于 Group 的，但 shape.attrs 需要存储去除 offset 后的值
      if (isInGroup && shape?.type === ShapeType.Image) {
        // 计算 pivot offset
        const actualSize = KonvaUtils.getImageActualSize(shape);
        const { pivotX, pivotY } = shape.attrs;
        const pivotOffsetX =
          pivotX !== undefined ? Number(pivotX) : actualSize.width / 2;
        const pivotOffsetY =
          pivotY !== undefined ? Number(pivotY) : actualSize.height / 2;

        // shape.attrs 应该存储去除 pivot offset 的值
        const shapeAttrX = _x - pivotOffsetX;
        const shapeAttrY = _y - pivotOffsetY;

        // console.log(
        //   '  isInGroup Image - pivotOffset:',
        //   pivotOffsetX,
        //   pivotOffsetY
        // );
        // console.log('  shape.attrs x, y:', shapeAttrX, shapeAttrY);

        shape?.setAttrs({
          x: shapeAttrX,
          y: shapeAttrY
        });

        // 直接更新 konva 节点到新位置
        if (node !== _node) {
          _node?.setAttrs({
            x: _x,
            y: _y
          });
        }
      } else {
        // 非成组或非图片的元素，使用原有逻辑
        shape?.setAttrs({
          x: _x,
          y: _y
        });

        // 更新konva上node的位置
        if (node !== _node) {
          // 对于图片类型，konva 节点的位置需要加上 pivotX/pivotY
          let nodeX = shape?.attrs?.x ?? 0;
          let nodeY = shape?.attrs?.y ?? 0;

          if (shape?.type === ShapeType.Image) {
            // 使用实际渲染宽高计算 offset，确保与 layout、setAttrs 逻辑一致
            const actualSize = KonvaUtils.getImageActualSize(shape);
            const { pivotX, pivotY } = shape.attrs;
            const offsetX =
              pivotX !== undefined ? Number(pivotX) : actualSize.width / 2;
            const offsetY =
              pivotY !== undefined ? Number(pivotY) : actualSize.height / 2;
            nodeX += offsetX;
            nodeY += offsetY;
          }
          const nodeData = _node?.getAttr?.('data') ?? {};
          const groupOffset = nodeData.progressBarGroupOffset as
            | { x: number; y: number }
            | undefined;
          // console.log('  konva node x, y:', nodeX, nodeY);

          _node?.setAttrs({
            x: groupOffset ? nodeX - groupOffset.x : nodeX,
            y: groupOffset ? nodeY - groupOffset.y : nodeY
          });
        }
      }
    });
    // console.log('=========================');
  }

  private testCanDrag = (node: Node, tr: Transformer) => {
    const res = {
      valid: false,
      msg: ''
    };
    if (tr.nodes().includes(node)) {
      res.valid = true;
    } else {
      node = KonvaUtils.getInteractableNode(node);
      res.valid =
        !!KonvaUtils.isNodeEnabled(node) && !!KonvaUtils.isNodeEnabled(node);
    }

    // 判断node对应的shape
    const shapeId = node.getAttr('attrs')?.name ?? '';
    if (res.valid) {
      const _res = Shape.getShape(shapeId)?.draggable();
      if (_res) {
        res.valid = _res?.valid;
        res.msg = _res?.msg;
      }
      if (lockedIdsInCurShapeTree.value.includes(shapeId)) {
        res.msg = '图层锁定，不可拖拽';
        res.valid = false;
      }
    }
    return res;
  };

  private testOutofThrethold(node: Node) {
    const res = this.getOffset(node);
    if (!res || !this.draggingNode) return;

    const { offsetX = 0, offsetY = 0 } = res;
    const { absoluteX0, absoluteY0 } = this.draggingNode;
    const threshold = KonvaDrag.THRESHOLD;
    if (Math.abs(offsetX) > threshold || Math.abs(offsetY) > threshold) {
      return;
    }
    node.absolutePosition({
      x: absoluteX0,
      y: absoluteY0
    });
  }

  bindEvent() {
    const stage = this.konvaService.stage;
    const layer = this.konvaService.layer;
    const tr = this.konvaService.tr;
    if (!stage || !layer || !tr) return;

    const konvaGuideline = new KonvaGuideline(this.konvaService);

    layer.on('dragstart', (e) => {
      if (KonvaTransform.TRANSFORMING) return;

      // 此时node有可能是Rect或者Transfotmer
      // 仅监听layer上的rect的移动
      const node = e.target as Node;
      if (node?.id() !== 'shape') return;
      // 获取需要更新位置的节点
      const _node = tr.nodes().includes(node)
        ? node
        : KonvaUtils.getInteractableNode(node);
      const moveNodes = tr.nodes().includes(_node) ? tr.nodes() : [_node];

      // 判断是否可拖拽
      const { valid, msg } = this.testCanDrag(node, tr);
      if (!valid) {
        showToast(msg ?? '元素不可拖拽');
        node.stopDrag();
        return;
      }

      this.setDragStart(node, moveNodes, tr);
      // 在拖拽图片元素时显示图片中心点
      const shape = Shape.getShape(node.name());
      if (shape?.type === ShapeType.Image) {
        this.konvaService.hover?.toggleHover(node.name(), true);
      }
    });

    /**
     * bind shape move
     */
    layer?.on('dragmove', (e) => {
      if (KonvaTransform.TRANSFORMING) return;

      // 此时node有可能是Rect或者Transfotmer
      // 仅监听layer上的rect的移动
      const node = e.target as Node;
      if (node?.id() !== 'shape') return;

      // 拖拽阈值
      this.testOutofThrethold(node);

      if (this.guideline) {
        // find possible snapping lines
        const _node = KonvaUtils.getInteractableNode(node);
        konvaGuideline?.drawGuides(_node, layer);
        this.setPosiOnDrag(node, stage);
      } else {
        this.setPosiOnDrag(node, stage);
      }

      // 在拖拽图片元素时更新中心点位置
      const shape = Shape.getShape(node.name());
      if (shape?.type === ShapeType.Image) {
        this.konvaService.hover?.updateCenterPoint(node.name());
      }
    });

    layer.on('dragend', (e) => {
      // clear all previous lines on the screen
      layer.find('.guide-line').forEach((l) => l.destroy());
      const draggingShapeIds = this.draggingShapes.map((item) => item.name);

      // 拖拽结束时隐藏图片中心点
      const node = e.target as Node;
      if (node?.id() === 'shape') {
        const shape = Shape.getShape(node.name());
        if (shape?.type === ShapeType.Image) {
          this.konvaService.hover?.toggleHover(node.name(), false);
        }
      }

      this.draggingShapes = [];
      this.draggingNode = undefined;
      KonvaDrag.DRAGGING = false;
      emitter.emit(EventType['konva:transformed'], {
        shapeIds: draggingShapeIds,
        dragend: true
      });
    });
  }
}
